import React, { useEffect, useRef, useState } from 'react';

const timelineData = [
  {
    time: '11:00',
    title: 'Check-in',
    description: 'Anchor down and get your badge before the hackathon adventure begins.',
    icon: '✓',
  },
  {
    time: '13:00',
    title: 'Opening Ceremony',
    description: 'Welcome aboard, meet the crew, and kick off Code It Up 6.0 in style.',
    icon: '🎉',
  },
  {
    time: '13:30',
    title: 'Panel Discussion',
    description: 'Hear insights from experts and get inspired for the coding journey ahead.',
    icon: '🎤',
  },
  {
    time: '14:30',
    title: 'Lunch Break',
    description: 'Refuel your energy with good food before diving back into the deep.',
    icon: '🍽️',
  },
  {
    time: '15:30',
    title: 'Round Table',
    description: 'Exchange ideas, strategies, and creative hacks with fellow teams.',
    icon: '💬',
  },
  {
    time: '17:00',
    title: 'Coffee Break',
    description: 'Quick recharge with coffee and conversations.',
    icon: '☕',
  },
  {
    time: '17:30',
    title: 'Workshop',
    description: 'Hands-on guidance to sharpen your tools for the big challenge.',
    icon: '⚙️',
  },
  {
    time: '18:30',
    title: 'Team Building',
    description: 'Strengthen your squad and sync up for maximum coding power.',
    icon: '🤝',
  },
  {
    time: '20:00',
    title: 'Challenge Announcement',
    description: 'The mission drops — it’s time to build, debug, and conquer.',
    icon: '📢',
  },
  {
    time: '22:00',
    title: 'Dinner',
    description: 'A well-deserved meal before the late-night coding waves.',
    icon: '🍽',
  },
  {
    time: '01:00',
    title: 'Snacks',
    description: 'Midnight snack station open to keep ideas flowing.',
    icon: '🍿',
  },
  {
    time: '03:00',
    title: 'Snacks',
    description: 'Another quick bite to power through the final stretch.',
    icon: '🍪',
  },
  {
    time: '08:00',
    title: 'Breakfast / End of Coding',
    description: 'Final commits in, breakfast served, coding phase complete.',
    icon: '🥐',
  },
  {
    time: '09:00',
    title: 'Technical Presentation',
    description: 'Show your build, explain your approach, and wow the judges.',
    icon: '💻',
  },
  {
    time: '11:00',
    title: 'Winners Announcement & Closing Ceremony',
    description: 'Celebrate the top teams and wrap up an unforgettable journey.',
    icon: '🏆',
  },
];

function Timeline() {
  const [visibleItems, setVisibleItems] = useState(new Set());
  const [isExpanded, setIsExpanded] = useState(false);
  const itemRefs = useRef([]);
  const toggleBtnRef = useRef(null);
  const beforeCollapseTopRef = useRef(null);
  const shouldAnchorAfterCollapseRef = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = Number(entry.target.dataset.index);
            setVisibleItems((prev) => new Set([...prev, index]));
          }
        });
      },
      { threshold: 0.3 }
    );

    itemRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isExpanded && shouldAnchorAfterCollapseRef.current) {
      const rafId = window.requestAnimationFrame(() => {
        const button = toggleBtnRef.current;
        const beforeTop = beforeCollapseTopRef.current;

        if (button && beforeTop !== null) {
          const afterTop = button.getBoundingClientRect().top;
          const delta = afterTop - beforeTop;
          const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

          if (Math.abs(delta) > 1) {
            window.scrollBy({ top: delta, left: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
          }

          button.focus({ preventScroll: true });
        }

        shouldAnchorAfterCollapseRef.current = false;
        beforeCollapseTopRef.current = null;
      });

      return () => window.cancelAnimationFrame(rafId);
    }
  }, [isExpanded]);

  const handleToggleTimeline = () => {
    const button = toggleBtnRef.current;

    if (isExpanded && button) {
      beforeCollapseTopRef.current = button.getBoundingClientRect().top;
      shouldAnchorAfterCollapseRef.current = true;
    } else {
      shouldAnchorAfterCollapseRef.current = false;
      beforeCollapseTopRef.current = null;
    }

    setIsExpanded((prev) => !prev);
  };

  return (
    <div className="timeline-section">
      <h1 style={{ textAlign: 'center' }}>OUR JOURNEY</h1>
      <div className={`timeline-container ${isExpanded ? 'timeline-expanded' : 'timeline-collapsed'}`}>
        <div className="timeline-line"></div>
        {timelineData.map((item, index) => (
          <div
            key={`${item.time}-${index}`}
            ref={(el) => (itemRefs.current[index] = el)}
            data-index={index}
            className={`timeline-item ${index % 2 === 0 ? 'timeline-left' : 'timeline-right'} ${
              visibleItems.has(index) ? 'timeline-visible' : ''
            }`}
          >
            <div className="timeline-dot">
              <span className="timeline-dot-icon">{item.icon}</span>
            </div>
            <div className="timeline-card">
              <div className="timeline-time-badge">{item.time}</div>
              <h3 className="timeline-card-title">{item.title}</h3>
              <p className="timeline-card-desc">{item.description}</p>
            </div>
          </div>
        ))}

        {!isExpanded && <div className="timeline-fade-overlay" aria-hidden="true"></div>}
      </div>

      <div className="timeline-toggle-wrap">
        <button
          ref={toggleBtnRef}
          type="button"
          className="timeline-toggle-btn"
          onClick={handleToggleTimeline}
          aria-expanded={isExpanded}
        >
          {isExpanded ? 'Show Compact Timeline' : 'Reveal Full Journey'}
        </button>
      </div>
    </div>
  );
}

export default Timeline;
